import React from "react";
import { ProgramPaymentType } from "../../../../types/dto/program";
import {
  programPaymenAcademy,
  programsStudentAcademy,
} from "../../../../shared/services";
import {
  Button,
  Card,
  Col,
  message,
  Modal,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import { useParams } from "react-router-dom";
import moment from "moment";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { formatNumberPrice } from "../../../../shared/utils/Helper";
import {
  PAYMENT_CONCEPTS,
  getPaymentConceptLabel,
  isCosmetologyProgramName,
  resolvePaymentConcept,
} from "../../../../shared/utils/academyPaymentConcepts";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../redux";
import { hasAcademyAdminAccess } from "../../../../shared/utils/roles";
import { ModalEditProgramPayment } from "../../../../shared/components/ui/modals/ModalEditProgramPayment";

const { Text, Title } = Typography;

function sumAmounts(payments: ProgramPaymentType[]) {
  return payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );
}

export const TablePersonalInfoStudentPayment = ({
  refreshKey = 0,
  onPaymentChange,
}: {
  refreshKey?: number;
  onPaymentChange?: () => void;
}) => {
  const { user_id, program_id } = useParams();
  const user = useSelector(selectUser);
  const canManagePayments = hasAcademyAdminAccess(user?.role);
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [paymentToEdit, setPaymentToEdit] =
    React.useState<ProgramPaymentType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingId, setLoadingId] = React.useState<number | null>(null);
  const [payments, setPayments] = React.useState<ProgramPaymentType[]>([]);
  const [hasCosmetologyProgram, setHasCosmetologyProgram] =
    React.useState(false);

  const fetchPayments = React.useCallback(async () => {
    if (!user_id || !program_id) return;

    setLoading(true);
    let loadedPayments: ProgramPaymentType[] = [];

    try {
      const paymentsRes = await programPaymenAcademy.find({
        query: {
          user_id: +user_id,
          program_id: +program_id,
          $limit: 200,
          $sort: { id: -1 },
        },
      });

      loadedPayments = paymentsRes.data || [];
      setPayments(loadedPayments);
    } catch {
      message.error("Error al cargar el historial de pagos");
      setPayments([]);
    } finally {
      setLoading(false);
    }

    try {
      const programsRes = await programsStudentAcademy.find({
        query: {
          user_id: +user_id,
          program_id: +program_id,
          $limit: 10,
        },
      });

      setHasCosmetologyProgram(
        (programsRes.data || []).some(
          (enrollment: { program?: { name?: string } }) =>
            isCosmetologyProgramName(enrollment.program?.name)
        )
      );
    } catch {
      setHasCosmetologyProgram(
        loadedPayments.some((payment) =>
          isCosmetologyProgramName(payment.program?.name)
        )
      );
    }
  }, [user_id, program_id]);

  React.useEffect(() => {
    fetchPayments();
  }, [fetchPayments, refreshKey]);

  const handleDelete = async (recordId: number) => {
    if (loadingId) return;

    const isConfirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: "¿Estás seguro de querer eliminar este pago?",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!isConfirmed) return;

    try {
      setLoadingId(recordId);
      await programPaymenAcademy.remove(recordId);
      message.success("Pago eliminado correctamente");
      await fetchPayments();
      onPaymentChange?.();
    } catch {
      message.error("Error al eliminar el pago");
    } finally {
      setLoadingId(null);
    }
  };

  const cuotaPayments = React.useMemo(
    () =>
      payments.filter(
        (payment) =>
          resolvePaymentConcept(payment.payment_concept) ===
          PAYMENT_CONCEPTS.CUOTA
      ),
    [payments]
  );

  const extraHoursPayments = React.useMemo(
    () =>
      payments.filter(
        (payment) =>
          resolvePaymentConcept(payment.payment_concept) ===
          PAYMENT_CONCEPTS.EXTRA_HOURS
      ),
    [payments]
  );

  const internshipPayments = React.useMemo(
    () =>
      payments.filter(
        (payment) =>
          resolvePaymentConcept(payment.payment_concept) ===
          PAYMENT_CONCEPTS.INTERNSHIP
      ),
    [payments]
  );

  const grandTotal = React.useMemo(() => sumAmounts(payments), [payments]);

  const handleEdit = (payment: ProgramPaymentType) => {
    setPaymentToEdit(payment);
    setOpenEditModal(true);
  };

  const handleEditSuccess = async () => {
    await fetchPayments();
    onPaymentChange?.();
  };

  const renderActions = (item: ProgramPaymentType) => {
    if (!canManagePayments) return null;

    return (
      <Space size="small">
        <Button
          onClick={() => handleEdit(item)}
          size="small"
          icon={<EditOutlined />}
          type="text"
        />
        <Button
          onClick={() => handleDelete(item.id)}
          size="small"
          icon={<DeleteOutlined />}
          danger
          type="text"
          loading={loadingId === item.id}
        />
      </Space>
    );
  };

  const actionColumn = canManagePayments
    ? [
        {
          title: "Acciones",
          key: "actions",
          render: renderActions,
        },
      ]
    : [];

  const cuotaColumns = [
    {
      title: "Periodo",
      key: "program_schedule_month",
      render: (_: unknown, item: ProgramPaymentType) =>
        item.program_schedule?.month
          ? moment(item.program_schedule.month).format("MMM / YYYY")
          : "—",
    },
    {
      title: "Fecha de abono",
      key: "payment_date",
      render: (_: unknown, item: ProgramPaymentType) =>
        item.payment_date
          ? moment(item.payment_date).format("DD/MM/YYYY")
          : "—",
    },
    {
      title: "Monto",
      key: "amount",
      render: (_: unknown, item: ProgramPaymentType) =>
        formatNumberPrice(+item.amount),
    },
    ...actionColumn,
  ];

  const supplementalColumns = [
    {
      title: "Fecha de abono",
      key: "payment_date",
      render: (_: unknown, item: ProgramPaymentType) =>
        item.payment_date
          ? moment(item.payment_date).format("DD/MM/YYYY")
          : "—",
    },
    {
      title: "Monto",
      key: "amount",
      render: (_: unknown, item: ProgramPaymentType) =>
        formatNumberPrice(+item.amount),
    },
    ...actionColumn,
  ];

  const renderPaymentSection = (
    title: string,
    sectionPayments: ProgramPaymentType[],
    columns: typeof supplementalColumns
  ) => (
    <Card
      size="small"
      title={title}
      style={{ marginBottom: 16 }}
      extra={
        <Text strong>
          Total abonado: {formatNumberPrice(sumAmounts(sectionPayments))}
        </Text>
      }
    >
      {sectionPayments.length > 0 ? (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={sectionPayments}
          columns={columns}
          scroll={{ x: true }}
        />
      ) : (
        <Text type="secondary">No hay abonos registrados.</Text>
      )}
    </Card>
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {renderPaymentSection(
        getPaymentConceptLabel(PAYMENT_CONCEPTS.CUOTA),
        cuotaPayments,
        cuotaColumns
      )}

      {renderPaymentSection(
        getPaymentConceptLabel(PAYMENT_CONCEPTS.EXTRA_HOURS),
        extraHoursPayments,
        supplementalColumns
      )}

      {hasCosmetologyProgram || internshipPayments.length > 0
        ? renderPaymentSection(
            getPaymentConceptLabel(PAYMENT_CONCEPTS.INTERNSHIP),
            internshipPayments,
            supplementalColumns
          )
        : null}

      <Col
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 8,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Total histórico: {formatNumberPrice(grandTotal)}
        </Title>
      </Col>

      <ModalEditProgramPayment
        openModal={openEditModal}
        setOpenModal={setOpenEditModal}
        payment={paymentToEdit}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
